import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Api } from './api';
import type { ClientDto, MonthlyReport, SessionDetail, SessionDto, SessionNoteDto, SessionType } from '../../../shared/types';
import { Icon, type IconName } from './icons';
import { dayKey, euro, fmtDate, fmtDay, fmtTime, fromLocalInput, hm, initials, monthLabel, shiftLocalInput, shiftMonth, toLocalInput, todayKey, typeLabel } from './format';
import { DateTimeInput } from './pickers';

// ---------------------------------------------------------------------------
// Hook e blocchi di base
// ---------------------------------------------------------------------------

export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!intervalMs) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function Panel({
  title,
  subtitle,
  right,
  children,
  className,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel${className ? ` ${className}` : ''}`}>
      <header className="panel-head">
        <div className="grow">
          <h2>{title}</h2>
          {subtitle && <div className="subtitle">{subtitle}</div>}
        </div>
        {right}
      </header>
      <div className="panel-body">{children}</div>
    </section>
  );
}

/** Voce di navigazione interna al pannello (stile "Unplanned 2 ›"). */
export function Seg({
  icon,
  label,
  count,
  tone,
  active,
  onClick,
}: {
  icon: IconName;
  label: React.ReactNode;
  count?: React.ReactNode;
  tone?: 'navy' | 'good';
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button type="button" className={`seg${active ? ' active' : ''}`} onClick={onClick}>
      <Icon name={icon} />
      <span className="lbl">{label}</span>
      {count != null && <span className={`count${tone ? ` ${tone}` : ''}`}>{count}</span>}
      <Icon name="chevronRight" size={18} className="chev" />
    </button>
  );
}

/** Sezione richiudibile (stile "^ Unplanned 2"). */
export function Section({
  title,
  count,
  open,
  onToggle,
  right,
  children,
}: {
  title: React.ReactNode;
  count?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <button type="button" className="section-head" onClick={onToggle}>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={18} />
        <span className="lbl">{title}</span>
        {right}
        {count != null && <span className="count">{count}</span>}
      </button>
      {open && <div className="section-body">{children}</div>}
    </div>
  );
}

export function useSections<K extends string>(initial: Record<K, boolean>) {
  const [open, setOpen] = useState(initial);
  const toggle = (k: K) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const set = (k: K, v: boolean) => setOpen((o) => (o[k] === v ? o : { ...o, [k]: v }));
  return { open, toggle, set };
}

export function Row({
  children,
  onClick,
  active,
  plain,
  className,
  style,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  plain?: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  const cls = ['row-item', onClick ? 'clickable' : '', active ? 'active' : '', plain ? 'plain' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <div className={cls} onClick={onClick} style={style}>
      {children}
    </div>
  );
}

export function Pill({
  icon,
  children,
  tone,
  className,
  title,
}: {
  icon?: IconName;
  children?: React.ReactNode;
  tone?: 'good' | 'warn' | 'bad' | 'info' | 'navy' | 'soft';
  className?: string;
  title?: string;
}) {
  return (
    <span className={`pill${tone ? ` ${tone}` : ''}${className ? ` ${className}` : ''}`} title={title}>
      {icon && <Icon name={icon} size={15} />}
      {children}
    </span>
  );
}

export function Handle() {
  return (
    <span className="handle">
      <Icon name="dots" size={16} />
    </span>
  );
}

export function Avatar({ name, navy, on, size }: { name: string; navy?: boolean; on?: boolean; size?: number }) {
  const style = size ? { width: size, height: size, fontSize: Math.round(size * 0.36) } : undefined;
  return (
    <span className={`avatar${navy ? ' navy' : ''}${on ? ' on' : ''}`} style={style} title={name}>
      {initials(name)}
    </span>
  );
}

export function StatusDot({ on }: { on: boolean }) {
  return <span className={`status-dot${on ? ' on' : ''}`} />;
}

export function Count({ n, tone }: { n: React.ReactNode; tone?: 'navy' | 'good' }) {
  return <span className={`count${tone ? ` ${tone}` : ''}`}>{n}</span>;
}

export function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`toggle${checked ? ' on' : ''}`}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}

export function Stat({ value, label, navy }: { value: React.ReactNode; label: React.ReactNode; navy?: boolean }) {
  return (
    <div className={`stat${navy ? ' navy' : ''}`}>
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

export function Empty({ icon = 'inbox', children }: { icon?: IconName; children: React.ReactNode }) {
  return (
    <div className="empty">
      <Icon name={icon} size={28} className="ico" />
      <div>{children}</div>
    </div>
  );
}

export function ErrorBanner({ message, onClose }: { message: string | null; onClose?: () => void }) {
  if (!message) return null;
  return (
    <div className="error">
      <span>{message}</span>
      {onClose && (
        <button type="button" className="btn icon small ghost" onClick={onClose} style={{ color: 'inherit' }}>
          <Icon name="x" size={16} />
        </button>
      )}
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

/**
 * Campi secondari di un modulo, chiusi di default: si aprono con un clic.
 * `open` iniziale: true quando qualcuno di quei campi ha già un valore, così in modifica si vede tutto.
 */
export function More({ label = 'Altri dettagli', open: initial = false, children }: { label?: string; open?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(initial);
  return (
    <div className={`more${open ? ' open' : ''}`}>
      <button type="button" className="more-toggle" onClick={() => setOpen((o) => !o)}>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} />
        {label}
      </button>
      {open && <div className="more-body">{children}</div>}
    </div>
  );
}

/** Titoletto dentro un modulo lungo (es. "Intestatario"). */
export function FormHeading({ children }: { children: React.ReactNode }) {
  return <div className="form-h">{children}</div>;
}

/** Campo password con occhio: un clic mostra la password e resta visibile finché non si preme di nuovo. */
export function PasswordInput({ style, ...props }: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [shown, setShown] = React.useState(false);
  return (
    <div className="pw" style={style}>
      <input {...props} type={shown ? 'text' : 'password'} />
      <button
        type="button"
        className="pw-eye"
        tabIndex={-1}
        title={shown ? 'Nascondi password' : 'Mostra password'}
        aria-label={shown ? 'Nascondi password' : 'Mostra password'}
        aria-pressed={shown}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShown((v) => !v)}
      >
        <Icon name={shown ? 'eyeOff' : 'eye'} size={17} />
      </button>
    </div>
  );
}

/** Campo "aggiungi" con suggerimento del tasto Invio (stile "Add todo, press ↵ ENTER to save"). */
export function AddInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  hint = 'INVIO per salvare',
  icon = 'plus',
  disabled,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  placeholder: string;
  hint?: string;
  icon?: IconName;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <form
      className="add-input"
      onSubmit={(e) => {
        e.preventDefault();
        if (!disabled && value.trim()) onSubmit();
      }}
    >
      <Icon name={icon} size={18} />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} autoFocus={autoFocus} />
      {!value && (
        <>
          <span className="key">↵</span>
          <span className="hint">{hint}</span>
        </>
      )}
    </form>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  narrow,
  resizable,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  narrow?: boolean;
  /** Come una finestra: si allarga dai bordi, si ingrandisce e va a schermo intero */
  resizable?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [max, setMax] = useState(false);
  const [full, setFull] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Esc esce prima dallo schermo intero (lo gestisce il sistema), poi chiude
      if (e.key === 'Escape' && !document.fullscreenElement) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    const on = () => setFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', on);
    return () => {
      document.removeEventListener('fullscreenchange', on);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, []);

  // Trascinando un bordo: la finestra resta centrata, quindi cresce dai due lati
  const startResize = (dir: string) => (e: React.PointerEvent) => {
    if (!box.current) return;
    e.preventDefault();
    const r = box.current.getBoundingClientRect();
    const x0 = e.clientX;
    const y0 = e.clientY;
    setDragging(true);
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - x0;
      const dy = ev.clientY - y0;
      let w = r.width;
      let h = r.height;
      if (dir.includes('e')) w = r.width + dx * 2;
      if (dir.includes('w')) w = r.width - dx * 2;
      if (dir.includes('s')) h = r.height + dy * 2;
      if (dir.includes('n')) h = r.height - dy * 2;
      setSize({ w: Math.max(420, Math.min(w, window.innerWidth - 16)), h: Math.max(320, Math.min(h, window.innerHeight - 16)) });
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const toggleFull = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void box.current?.requestFullscreen().catch(() => setMax(true));
  };

  const style = resizable && size && !max ? { width: size.w, height: size.h, maxHeight: 'none' } : undefined;
  const cls = ['modal', narrow ? 'narrow' : '', resizable ? 'resizable' : '', max ? 'max' : '', full ? 'full' : '', dragging ? 'dragging' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={cls} ref={box} style={style}>
        <div className="modal-header" onDoubleClick={resizable ? () => setMax((m) => !m) : undefined}>
          <h3>{title}</h3>
          <div className="modal-tools">
            {resizable && !full && (
              <button type="button" className="btn icon ghost" onClick={() => setMax((m) => !m)} title={max ? 'Ripristina' : 'Ingrandisci'}>
                <Icon name={max ? 'restore' : 'maximize'} size={16} />
              </button>
            )}
            {resizable && (
              <button type="button" className="btn icon ghost" onClick={toggleFull} title={full ? 'Esci da schermo intero' : 'Schermo intero'}>
                <Icon name={full ? 'restore' : 'fullscreen'} size={17} />
              </button>
            )}
            <button type="button" className="btn icon ghost" onClick={onClose} title="Chiudi">
              <Icon name="x" size={18} />
            </button>
          </div>
        </div>
        {children}
        {footer && <div className="modal-footer">{footer}</div>}
        {resizable &&
          !max &&
          !full &&
          ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'].map((d) => <span key={d} className={`modal-grip ${d}`} onPointerDown={startResize(d)} />)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Menu contestuale (tasto destro)
// ---------------------------------------------------------------------------

export interface MenuItem {
  icon?: IconName;
  label: string;
  onClick: () => void;
  danger?: boolean;
  /** Separatore prima della voce. */
  sep?: boolean;
}

export interface MenuState {
  x: number;
  y: number;
  title?: string;
  items: MenuItem[];
}

/** Apre un menu con il tasto destro (o da un pulsante) accanto al punto cliccato. */
export function useMenu() {
  const [menu, setMenu] = useState<MenuState | null>(null);
  const open = useCallback((e: React.MouseEvent, items: MenuItem[], title?: string) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, title, items });
  }, []);
  const close = useCallback(() => setMenu(null), []);
  return { menu, open, close };
}

export function ContextMenu({ menu, onClose }: { menu: MenuState | null; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!menu) return;
    const el = ref.current;
    const w = el?.offsetWidth ?? 220;
    const h = el?.offsetHeight ?? 200;
    setPos({
      x: Math.max(8, Math.min(menu.x, window.innerWidth - w - 8)),
      y: Math.max(8, Math.min(menu.y, window.innerHeight - h - 8)),
    });
  }, [menu]);

  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const onScroll = () => onClose();
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    window.addEventListener('wheel', onScroll, { passive: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('wheel', onScroll);
    };
  }, [menu, onClose]);

  if (!menu) return null;
  // Il menu vive in fondo al body: cosi non viene tagliato dai pannelli traslucidi.
  return createPortal(
    <div className="ctx-layer" onMouseDown={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }}>
      <div
        className="ctx-menu"
        ref={ref}
        style={{ left: pos.x, top: pos.y }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {menu.title && <div className="ctx-title">{menu.title}</div>}
        {menu.items.map((item, i) => (
          <React.Fragment key={item.label}>
            {item.sep && i > 0 && <div className="ctx-sep" />}
            <button
              type="button"
              className={`ctx-item${item.danger ? ' danger' : ''}`}
              onClick={() => {
                onClose();
                item.onClick();
              }}
            >
              {item.icon && <Icon name={item.icon} size={16} />}
              {item.label}
            </button>
          </React.Fragment>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Correzione degli orari di una sessione: capita di far partire il cronometro
 * qualche minuto dopo aver iniziato davvero, o di fermarlo in ritardo.
 */
export function TimeEditModal({
  startedAt,
  endedAt,
  title = 'Correggi gli orari',
  onClose,
  onSave,
}: {
  startedAt: string;
  endedAt?: string | null;
  title?: string;
  onClose: () => void;
  onSave: (startedAt: string, endedAt: string | null) => Promise<void>;
}) {
  const [start, setStart] = useState(() => toLocalInput(startedAt));
  const [end, setEnd] = useState(() => (endedAt ? toLocalInput(endedAt) : ''));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const now = useNow(30_000);

  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : now;
  const minutes = Number.isFinite(startMs) && endMs > startMs ? Math.round((endMs - startMs) / 60_000) : 0;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave(fromLocalInput(start), end ? fromLocalInput(end) : null);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={title}
      narrow
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={save} disabled={busy || !start || minutes <= 0}>
            <Icon name="check" size={16} /> {busy ? 'Salvataggio\u2026' : 'Salva orari'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 4 }}>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <Field label="Inizio">
          <DateTimeInput value={start} onChange={setStart} />
        </Field>
        <div className="hstack wrap" style={{ gap: 6, marginTop: -4, marginBottom: 10 }}>
          {[-30, -15, -10, -5, 5].map((n) => (
            <button key={n} type="button" className="btn small" onClick={() => setStart((v) => shiftLocalInput(v, n))}>
              {n > 0 ? `+${n}` : n} min
            </button>
          ))}
        </div>
        {endedAt && (
          <>
            <Field label="Fine">
              <DateTimeInput value={end} onChange={setEnd} />
            </Field>
            <div className="hstack wrap" style={{ gap: 6, marginTop: -4, marginBottom: 10 }}>
              {[-30, -15, -5, 5, 15, 30].map((n) => (
                <button key={n} type="button" className="btn small" onClick={() => setEnd((v) => shiftLocalInput(v, n))}>
                  {n > 0 ? `+${n}` : n} min
                </button>
              ))}
            </div>
            <Field label="Oppure imposta la durata" hint="La fine viene spostata di conseguenza.">
              <div className="hstack wrap" style={{ gap: 6 }}>
                {[15, 30, 60, 120, 240, 480].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`btn small${minutes === n ? ' dark' : ''}`}
                    onClick={() => setEnd(shiftLocalInput(start, n))}
                  >
                    {hm(n)}
                  </button>
                ))}
              </div>
            </Field>
          </>
        )}
        <div className="notice-pill" style={{ alignSelf: 'flex-start' }}>
          <Icon name="clock" size={15} />
          {endedAt ? 'Durata conteggiata' : 'Tempo gi\u00e0 trascorso'}: <b>{hm(minutes)}</b>
        </div>
        <div className="muted tiny" style={{ marginTop: 6 }}>
          Gli orari seguono l'orologio di questo PC.
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Orologio e calendario
// ---------------------------------------------------------------------------

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

export function ClockFace({ now }: { now: number }) {
  const d = new Date(now);
  const s = d.getSeconds();
  const m = d.getMinutes() + s / 60;
  const h = (d.getHours() % 12) + m / 60;
  const ticks = [];
  for (let i = 0; i < 60; i++) {
    const major = i % 5 === 0;
    const [x1, y1] = polar(60, 60, major ? 47 : 50, i * 6);
    const [x2, y2] = polar(60, 60, 53, i * 6);
    ticks.push(
      <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} style={{ stroke: major ? 'var(--clock-ink)' : 'var(--clock-tick)' }} strokeWidth={major ? 1.6 : 1} strokeLinecap="round" />,
    );
  }
  return (
    <svg viewBox="0 0 120 120" className="clock-face" aria-hidden="true">
      <circle cx="60" cy="60" r="57" style={{ fill: 'var(--clock-face)', stroke: 'var(--clock-ring)' }} />
      {ticks}
      <g transform={`rotate(${h * 30} 60 60)`}>
        <path d="M60 67 L56.8 60 L60 31 L63.2 60 Z" style={{ fill: 'var(--clock-face)', stroke: 'var(--clock-ink)' }} strokeWidth="1.6" strokeLinejoin="round" />
      </g>
      <g transform={`rotate(${m * 6} 60 60)`}>
        <path d="M60 69 L57.4 60 L60 18 L62.6 60 Z" style={{ fill: 'var(--clock-face)', stroke: 'var(--clock-ink)' }} strokeWidth="1.6" strokeLinejoin="round" />
      </g>
      <g transform={`rotate(${s * 6} 60 60)`}>
        <line x1="60" y1="70" x2="60" y2="15" stroke="#8fa6cf" strokeWidth="1.2" strokeLinecap="round" />
      </g>
      <circle cx="60" cy="60" r="3.2" style={{ fill: 'var(--clock-ink)' }} />
    </svg>
  );
}

export function ClockCard({ now, headline, sub, on }: { now: number; headline: React.ReactNode; sub: React.ReactNode; on?: boolean }) {
  return (
    <div className={`clock-card${on ? ' on' : ''}`}>
      <ClockFace now={now} />
      <div className="grow">
        <div className="big timer">{headline}</div>
        <div className="sub">{sub}</div>
      </div>
    </div>
  );
}

export function MonthNav({ value, onChange, className }: { value: string; onChange: (m: string) => void; className?: string }) {
  return (
    <div className={`cal-head${className ? ` ${className}` : ''}`}>
      <span>{monthLabel(value)}</span>
      <span className="nav">
        <button type="button" onClick={() => onChange(shiftMonth(value, -1))} title="Mese precedente">
          <Icon name="chevronLeft" size={18} />
        </button>
        <button type="button" onClick={() => onChange(shiftMonth(value, 1))} title="Mese successivo">
          <Icon name="chevronRight" size={18} />
        </button>
      </span>
    </div>
  );
}

/** Nome del giorno e, sotto, gli impegni (uno per riga, con il pallino). */
function dayTitle(date: Date, items?: string[]): string {
  const head = date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
  if (!items?.length) return head;
  const rows = items.slice(0, 6).map((x) => '• ' + x);
  if (items.length > 6) rows.push('• … e altri ' + (items.length - 6));
  return [head, ...rows].join(String.fromCharCode(10));
}

/** Pallini di un giorno: impegno (sempre), con un collega (verde), prenotato da altri nel calendario condiviso (rosa). */
export type DayMark = { together?: boolean; booked?: boolean };

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

/** Calendario mensile (settimana da lunedì). `marks` = giorni con attività (chiave YYYY-MM-DD): un pallino, o due (il secondo verde) se c'è qualcosa da fare insieme a un collega. */
export function Calendar({
  month,
  selected,
  marks,
  onSelect,
  onMonth,
  legend = [{ label: 'Giorno con attività' }],
  details,
}: {
  month: string;
  selected?: string | null;
  marks: Record<string, unknown>;
  /** Cosa significano i pallini: il secondo (verde) si indica con `together` */
  legend?: { label: string; together?: boolean; booked?: boolean }[];
  /** Per ogni giorno, gli impegni da mostrare nel suggerimento sotto il nome del giorno */
  details?: Record<string, string[]>;
  onSelect: (day: string) => void;
  onMonth: (month: string) => void;
}) {
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const offset = (first.getDay() + 6) % 7;
  const today = todayKey();
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const date = new Date(y, m - 1, 1 - offset + i);
    const key = dayKey(date);
    const cls = [
      'cal-day',
      date.getMonth() !== m - 1 ? 'other' : '',
      key === today ? 'today' : '',
      key < today ? 'past' : '',
      key === selected ? 'selected' : '',
      marks[key] ? 'has' : '',
    ]
      .filter(Boolean)
      .join(' ');
    cells.push(
      <button type="button" key={key} className={cls} onClick={() => onSelect(key)} title={dayTitle(date, details?.[key])}>
        {date.getDate()}
        {marks[key] ? (
          <span className="cal-dots" aria-hidden>
            <i />
            {(marks[key] === 'together' || (marks[key] as DayMark)?.together) && <i className="together" />}
            {(marks[key] as DayMark)?.booked && <i className="booked" />}
          </span>
        ) : null}
      </button>,
    );
  }
  return (
    <div>
      <MonthNav value={month} onChange={onMonth} />
      <div className="cal">
        <div className="cal-grid">
          {WEEKDAYS.map((w) => (
            <div key={w} className="wd">
              {w}
            </div>
          ))}
          {cells}
        </div>
        <div className="cal-legend">
          {legend.map((l) => (
            <span key={l.label}>
              <i className={l.together ? 'together' : l.booked ? 'booked' : ''} /> {l.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Note, sessioni e report
// ---------------------------------------------------------------------------

export function NoteRow({ note }: { note: SessionNoteDto }) {
  return (
    <Row>
      <Handle />
      <Pill icon="checkCircle">{fmtTime(note.createdAt)}</Pill>
      <span className="grow note-text">{note.body}</span>
    </Row>
  );
}

function SessionDetailBox({ detail }: { detail: SessionDetail }) {
  return (
    <div className="row-detail stack" style={{ gap: 8 }}>
      {detail.summary && (
        <div>
          <div className="eyebrow">Riepilogo</div>
          <p className="pre">{detail.summary}</p>
        </div>
      )}
      <div>
        <div className="eyebrow">Note ({detail.notes.length})</div>
        {detail.notes.length === 0 ? (
          <span className="muted small">Nessuna nota.</span>
        ) : (
          <div className="stack" style={{ gap: 6, marginTop: 6 }}>
            {detail.notes.map((n) => (
              <div key={n.id} className="hstack top">
                <span className="pill soft">{fmtTime(n.createdAt)}</span>
                <span className="note-text">{n.body}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="muted small">
        {fmtDate(detail.startedAt)} · base {hm(detail.baseMinutes)} · extra {hm(detail.extraMinutes)}
      </div>
    </div>
  );
}

export function SessionRow({
  api,
  session,
  showAmount = true,
  showClient = true,
  onDelete,
  onEdit,
  onMenu,
}: {
  api: Api;
  session: SessionDto;
  showAmount?: boolean;
  showClient?: boolean;
  onDelete?: (s: SessionDto) => void;
  /** Se presente, la riga mostra il pulsante per correggere gli orari. */
  onEdit?: (s: SessionDto) => void;
  /** Tasto destro sulla riga: apre il menu con tutte le azioni. */
  onMenu?: (s: SessionDto, e: React.MouseEvent) => void;
}) {
  const [detail, setDetail] = useState<SessionDetail | null | undefined>(undefined);
  const open = detail !== undefined;

  const toggle = async () => {
    if (open) {
      setDetail(undefined);
      return;
    }
    setDetail(null);
    try {
      setDetail(await api.get<SessionDetail>(`/api/reports/sessions/${session.id}`));
    } catch {
      setDetail({ ...session, notes: [] });
    }
  };

  const s = session;
  return (
    <div onContextMenu={onMenu ? (e) => onMenu(s, e) : undefined}>
      <Row onClick={toggle} active={open}>
        <Handle />
        <Pill icon={s.type === 'call' ? 'phone' : 'checkCircle'} tone={s.type === 'call' ? 'warn' : undefined}>
          {fmtTime(s.startedAt)}
          {s.endedAt ? `–${fmtTime(s.endedAt)}` : ''}
        </Pill>
        <div className="grow">
          <div className="title">{showClient ? s.clientName : s.summary || typeLabel(s.type)}</div>
          <div className="sub">
            {fmtDate(s.startedAt)}
            {showClient || s.summary ? ` · ${typeLabel(s.type)}` : ''}
            {showClient && s.summary ? ` · ${s.summary}` : ''}
          </div>
        </div>
        <span className="pill soft">{hm(s.totalMinutes)}</span>
        {showAmount && <span className="pill">{euro(s.amountCents)}</span>}
        {onEdit && (
          <button
            type="button"
            className="btn icon small ghost"
            title="Correggi gli orari"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(s);
            }}
          >
            <Icon name="clock" size={16} />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            className="btn icon small ghost"
            title="Elimina sessione"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(s);
            }}
          >
            <Icon name="trash" size={16} />
          </button>
        )}
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} className="muted" />
      </Row>
      {open && (detail === null ? <div className="row-detail muted small">Caricamento…</div> : <SessionDetailBox detail={detail} />)}
    </div>
  );
}

export function SessionsList({
  api,
  sessions,
  showAmount = true,
  showClient = true,
  onDelete,
  onEdit,
  onMenu,
  emptyText = 'Nessuna sessione nel periodo.',
}: {
  api: Api;
  sessions: SessionDto[];
  showAmount?: boolean;
  showClient?: boolean;
  onDelete?: (s: SessionDto) => void;
  onEdit?: (s: SessionDto) => void;
  onMenu?: (s: SessionDto, e: React.MouseEvent) => void;
  emptyText?: string;
}) {
  if (sessions.length === 0) return <Empty icon="calendar">{emptyText}</Empty>;
  return (
    <div className="stack" style={{ gap: 8 }}>
      {sessions.map((s) => (
        <SessionRow
          key={s.id}
          api={api}
          session={s}
          showAmount={showAmount}
          showClient={showClient}
          onDelete={onDelete}
          onEdit={onEdit}
          onMenu={onMenu}
        />
      ))}
    </div>
  );
}

export function reportText(r: MonthlyReport): string {
  const lines = [
    `Riepilogo ${r.client.name} — ${monthLabel(r.month)}`,
    `Ore totali: ${hm(r.totalMinutes)} (base ${hm(r.baseMinutes)}, extra ${hm(r.extraMinutes)})`,
    `Importo: ${euro(r.amountCents)}`,
    '',
  ];
  if (r.commissions) {
    lines.push(
      `Provvigione segnalazione (${r.commissions.referral.pct}%): ${euro(r.commissions.referral.cents)}`,
      `Provvigione gestione (${r.commissions.management.pct}%): ${euro(r.commissions.management.cents)}`,
      '',
    );
  }
  lines.push('Sessioni:');
  for (const s of r.sessions) {
    lines.push(
      `- ${fmtDate(s.startedAt)} ${fmtTime(s.startedAt)}–${s.endedAt ? fmtTime(s.endedAt) : ''} · ${typeLabel(s.type)} · ${hm(
        s.totalMinutes,
      )} · ${euro(s.amountCents)}${s.summary ? ` · ${s.summary}` : ''}`,
    );
  }
  if (r.days.length) {
    lines.push('', 'Per giornata:');
    for (const d of r.days) lines.push(`- ${fmtDay(d.day)}: ${hm(d.minutes)} · ${euro(d.amountCents)}`);
  }
  return lines.join('\n');
}

/** Dettaglio mensile di un cliente: numeri, provvigioni, pulsante copia e lista sessioni. */
export function ReportDetail({
  api,
  clientId,
  month,
  showCommissions,
  showAmount = true,
}: {
  api: Api;
  clientId: number;
  month: string;
  showCommissions: boolean;
  showAmount?: boolean;
}) {
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setError(null);
    api
      .get<MonthlyReport>(`/api/reports/monthly?clientId=${clientId}&month=${month}`)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [api, clientId, month]);

  const copy = async () => {
    if (!report) return;
    await navigator.clipboard.writeText(reportText(report));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (error) return <ErrorBanner message={error} />;
  if (!report) return <div className="muted small">Caricamento…</div>;

  return (
    <div className="stack">
      <div className="hstack between wrap">
        <div>
          <div className="eyebrow">{monthLabel(report.month)}</div>
          <div style={{ fontSize: 18, fontWeight: 500 }}>{report.client.name}</div>
        </div>
        {showAmount && (
          <button type="button" className="btn small" title="Copia ore e importi del mese, da incollare nella fattura" onClick={copy}>
            <Icon name="copy" size={16} /> {copied ? 'Copiato!' : 'Copia riepilogo per fattura'}
          </button>
        )}
      </div>
      <div className="stats">
        <Stat value={hm(report.totalMinutes)} label="ore totali" />
        <Stat value={hm(report.baseMinutes)} label="a tariffa base" />
        <Stat value={hm(report.extraMinutes)} label="a tariffa extra" />
        {showAmount && <Stat value={euro(report.amountCents)} label="importo" navy />}
      </div>
      {showCommissions && report.commissions && (
        <div className="stats">
          <Stat
            value={euro(report.commissions.referral.cents)}
            label={`segnalazione ${report.commissions.referral.pct}%${report.commissions.referral.userName ? ` · ${report.commissions.referral.userName}` : ''}`}
          />
          <Stat
            value={euro(report.commissions.management.cents)}
            label={`gestione ${report.commissions.management.pct}%${report.commissions.management.userName ? ` · ${report.commissions.management.userName}` : ''}`}
          />
        </div>
      )}
      <div className="eyebrow">Sessioni ({report.sessions.length})</div>
      <SessionsList api={api} sessions={report.sessions} showAmount={showAmount} showClient={false} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dettagli sessione e inserimento manuale
// ---------------------------------------------------------------------------

/** Cambia cliente, tipo e riepilogo di una sessione (anche di quella in corso). */
export function SessionMetaModal({
  session,
  clients,
  running,
  onClose,
  onSave,
}: {
  session: SessionDto;
  clients: ClientDto[];
  running?: boolean;
  onClose: () => void;
  onSave: (patch: { clientId: number; type: SessionType; summary?: string | null }) => Promise<void>;
}) {
  const [clientId, setClientId] = useState(String(session.clientId));
  const [type, setType] = useState<SessionType>(session.type);
  const [summary, setSummary] = useState(session.summary ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({ clientId: Number(clientId), type, ...(running ? {} : { summary: summary.trim() || null }) });
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const options = clients.filter((c) => c.active || c.id === session.clientId);
  const moved = Number(clientId) !== session.clientId;

  return (
    <Modal
      title="Dettagli della sessione"
      narrow
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={save} disabled={busy}>
            <Icon name="check" size={16} /> {busy ? 'Salvataggio\u2026' : 'Salva'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 4 }}>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <Field label="Cliente" hint={moved ? 'La sessione viene ricalcolata con le tariffe del nuovo cliente.' : undefined}>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            {options.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Tipo">
          <select value={type} onChange={(e) => setType(e.target.value as SessionType)}>
            <option value="work">Lavoro</option>
            <option value="call">Telefonata</option>
          </select>
        </Field>
        {!running && (
          <Field label="Riepilogo per il cliente">
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} style={{ minHeight: 80 }} />
          </Field>
        )}
      </div>
    </Modal>
  );
}

/** Sessione dimenticata: si inserisce a mano indicando cliente, inizio e fine. */
export function ManualSessionModal({
  clients,
  onClose,
  onSave,
}: {
  clients: ClientDto[];
  onClose: () => void;
  onSave: (input: { clientId: number; type: SessionType; startedAt: string; endedAt: string; summary: string | null }) => Promise<void>;
}) {
  const active = clients.filter((c) => c.active);
  const [clientId, setClientId] = useState(active[0] ? String(active[0].id) : '');
  const [type, setType] = useState<SessionType>('work');
  const [start, setStart] = useState(() => toLocalInput(new Date(Date.now() - 3_600_000).toISOString()));
  const [end, setEnd] = useState(() => toLocalInput(new Date().toISOString()));
  const [summary, setSummary] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const minutes = Number.isFinite(startMs) && endMs > startMs ? Math.round((endMs - startMs) / 60_000) : 0;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({
        clientId: Number(clientId),
        type,
        startedAt: fromLocalInput(start),
        endedAt: fromLocalInput(end),
        summary: summary.trim() || null,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Aggiungi una sessione"
      narrow
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={save} disabled={busy || !clientId || minutes <= 0}>
            <Icon name="check" size={16} /> {busy ? 'Salvataggio\u2026' : 'Aggiungi'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 4 }}>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <Field label="Cliente">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            {active.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Tipo">
          <select value={type} onChange={(e) => setType(e.target.value as SessionType)}>
            <option value="work">Lavoro</option>
            <option value="call">Telefonata</option>
          </select>
        </Field>
        <Field label="Inizio">
          <DateTimeInput value={start} onChange={setStart} />
        </Field>
        <Field label="Fine">
          <DateTimeInput value={end} onChange={setEnd} />
        </Field>
        <div className="hstack wrap" style={{ gap: 6, marginTop: -4, marginBottom: 10 }}>
          {[15, 30, 60, 120, 240, 480].map((n) => (
            <button
              key={n}
              type="button"
              className={`btn small${minutes === n ? ' dark' : ''}`}
              onClick={() => setEnd(shiftLocalInput(start, n))}
            >
              {hm(n)}
            </button>
          ))}
        </div>
        <Field label="Riepilogo per il cliente">
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} style={{ minHeight: 70 }} />
        </Field>
        <div className="notice-pill" style={{ alignSelf: 'flex-start' }}>
          <Icon name="clock" size={15} />
          Durata: <b>{hm(minutes)}</b>
        </div>
      </div>
    </Modal>
  );
}
